/**
 * Formatting utilities for Frontend/Theme module
 *
 * Pure functions for formatting prices, text, and other display values.
 */

/**
 * Decode HTML entities in a string.
 * Uses a textarea element which safely decodes entities without executing scripts.
 */
export function decodeHtml(html: string): string {
  const txt = document.createElement('textarea')
  txt.innerHTML = html
  return txt.value
}

/**
 * Format price in Romanian locale (Lei)
 *
 * @param price - Price value in Lei
 * @returns Formatted price string (e.g., "1.234,56 Lei")
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency: 'Lei',
  }).format(price)
}

/**
 * Calculate discount amount between regular and special price
 *
 * @param regularPrice - Original price
 * @param specialPrice - Discounted price (null/undefined means no discount)
 * @returns Discount amount (0 if no discount)
 */
export function calculateDiscount(
  regularPrice: number,
  specialPrice: number | null | undefined
): number {
  if (!specialPrice || specialPrice >= regularPrice) return 0
  return regularPrice - specialPrice
}

/**
 * Truncate text to max length with ellipsis
 * Default max is 70 chars
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 70)
 * @returns Truncated text with "..." if needed
 */
export function truncateText(text: string, maxLength = 70): string {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength - 3)}...`
}

/**
 * Truncate product name (alias for truncateText with 70 char default)
 */
export function truncateName(name: string): string {
  return truncateText(name, 70)
}

// =============================================================================
// GRAPHQL PRODUCT TRANSFORMATION
// =============================================================================

/** Category from GraphQL product */
interface GraphQLProductCategory {
  id: number
  name?: string
  url_path?: string
}

/** GraphQL product type for mapGraphQLProductToProduct */
export interface GraphQLProduct {
  id?: number | string
  sku?: string
  name?: string
  type_id?: string
  reward_points?: number
  url_rewrites?: { url: string }[]
  price_range?: {
    minimum_price?: {
      regular_price?: { value?: number }
      final_price?: { value?: number }
    }
  }
  customProductImageUrl?: { url?: string }
  custom_attributes?: { attribute_code: string; value: string }[]
  sameday_locker_delivery?: boolean | number | string | null
  special_text_promo?: string | null
  text_from_date?: string | null
  text_to_date?: string | null
  new_from_date?: string | null
  new_to_date?: string | null
  categories?: GraphQLProductCategory[]
}

/** Valid Magento product types */
const VALID_PRODUCT_TYPES = ['simple', 'configurable', 'grouped', 'bundle', 'virtual', 'downloadable'] as const
type ValidProductType = (typeof VALID_PRODUCT_TYPES)[number]

/** Category ID for discontinued products */
const DISCONTINUED_CATEGORY_ID = 470

/** Type guard for valid product types */
function isValidProductType(value: unknown): value is ValidProductType {
  return typeof value === 'string' && VALID_PRODUCT_TYPES.includes(value as ValidProductType)
}

/**
 * Find custom attribute value from product custom_attributes array
 */
function findCustomAttributeValue(
  customAttributes: GraphQLProduct['custom_attributes'],
  attributeCode: string
): string | null {
  if (!customAttributes) return null
  const attribute = customAttributes.find(
    (attr) => attr.attribute_code === attributeCode
  )
  return attribute ? attribute.value : null
}

/**
 * Check if gift badge should be shown
 * (special_text_promo exists and current date is within from/to range)
 */
function shouldShowGiftBadge(product: GraphQLProduct): boolean {
  if (!product.special_text_promo) return false

  const now = new Date()
  const toDate = product.text_to_date ? new Date(product.text_to_date) : null
  const fromDate = product.text_from_date ? new Date(product.text_from_date) : null

  if (toDate && toDate < now) return false
  if (fromDate && fromDate > now) return false

  return true
}

/**
 * Check if "New" badge should be shown
 * (new_from_date/new_to_date within valid date range)
 * Mirrors Eadesign\ProductFlags\ViewModel\Tags::isNewTag logic
 */
function shouldShowNewBadge(product: GraphQLProduct): boolean {
  if (!product.new_from_date || !product.new_to_date) return false

  const now = new Date()
  const fromDate = new Date(product.new_from_date)
  const toDate = new Date(product.new_to_date)

  return now >= fromDate && now <= toDate
}

import type { Product } from '../../UiBlocks'

/**
 * Map GraphQL product to ProductCard Product interface
 *
 * Transforms raw GraphQL product data (from Magento queries) to the
 * simplified Product interface used by ProductCard component.
 *
 * Handles: prices, MSRP, product type, h2 name, reward points,
 * gift badge, locker delivery, stock status, discontinued flag.
 *
 * @param product - Raw GraphQL product object
 * @returns Product interface for ProductCard
 */
export function mapGraphQLProductToProduct(product: GraphQLProduct): Product {
  const baseUrl = typeof window !== 'undefined' ? (window as { BASE_URL?: string }).BASE_URL || '' : ''
  const urlRewrite = product.url_rewrites?.[0]?.url || ''

  const regularPrice = product.price_range?.minimum_price?.regular_price?.value ?? 0
  const finalPrice = product.price_range?.minimum_price?.final_price?.value ?? regularPrice
  const msrpValue = parseFloat(
    findCustomAttributeValue(product.custom_attributes, 'msrp') || '0'
  )

  // Only set specialPrice if it's different from regular price (actual discount)
  const specialPrice = finalPrice < regularPrice ? finalPrice : null

  // Product availability from custom attributes
  const cuFaraPret = findCustomAttributeValue(product.custom_attributes, 'cu_fara_pret')
  const isInStockAttr = findCustomAttributeValue(product.custom_attributes, 'is_in_stock')
  const h2ProductName = findCustomAttributeValue(product.custom_attributes, 'h2_product_name')

  return {
    id: String(product.id ?? product.sku ?? ''),
    sku: product.sku ?? '',
    name: product.name ?? '',
    url: `${baseUrl}${urlRewrite}`,
    imageUrl: product.customProductImageUrl?.url ?? '',
    price: regularPrice,
    specialPrice,
    msrp: msrpValue > 0 ? msrpValue : null,
    productType: isValidProductType(product.type_id) ? product.type_id : 'simple',
    h2ProductName: h2ProductName || null,
    rewardPoints: product.reward_points ?? null,
    isNew: shouldShowNewBadge(product),
    showGiftBadge: shouldShowGiftBadge(product),
    showLockerDelivery: !!product.sameday_locker_delivery,
    isInStock: isInStockAttr ? isInStockAttr === 'In Stock' : true,
    showAddToCart: cuFaraPret ? cuFaraPret === 'Da' : true,
    isNotForSale: cuFaraPret === 'Nu',
    isDiscontinued: product.categories?.some(cat => cat.id === DISCONTINUED_CATEGORY_ID) ?? false,
  }
}
